import styled, { css, keyframes } from "styled-components";

const highlightFade = keyframes`
  0%   { opacity: 0.11; }
  60%  { opacity: 0.11; }
  100% { opacity: 0; }
`;

export const Row = styled.div<{
  $background?: string;
  $hoverBackground?: string;
  $linkedHover?: boolean;
  $clickable?: boolean;
}>`
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--spacing-sm, 8px);
  min-height: 64px;
  padding: 0 var(--spacing-md, 12px);
  cursor: ${({ $clickable }) => ($clickable ? "pointer" : "default")};
  /* Linked hover mirrors the matching row's hover shade (same rules as
     :hover below): grey for plain rows, a darker same-tone shade for rows
     with a change tint. */
  background: ${({ $background, $hoverBackground, $linkedHover }) =>
    ($linkedHover && $hoverBackground) || $background || "transparent"};

  &:hover {
    background: ${({ $background, $hoverBackground }) =>
      $hoverBackground || $background || "transparent"};
  }

  &:not(:last-child) {
    border-bottom: 1px solid var(--color-gray-100, #e2e3e5);
  }

  /* The list card does not clip overflow, so tinted row backgrounds round
     their own corners to stay inside the card's radius. */
  &:first-child {
    border-radius: var(--radius-md, 8px) var(--radius-md, 8px) 0 0;
  }
  &:last-child {
    border-radius: 0 0 var(--radius-md, 8px) var(--radius-md, 8px);
  }
`;

export const AccentBar = styled.span<{ $color: string }>`
  position: absolute;
  left: 0;
  top: 16px;
  bottom: 16px;
  width: 3px;
  border-radius: 0 3px 3px 0;
  background: ${({ $color }) => $color};
`;

export const Position = styled.span<{ $highlighted?: boolean }>`
  width: 25px;
  flex-shrink: 0;
  text-align: left;
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1rem;
  /* Rows with a change indicator use the stronger foreground tone (Figma). */
  color: ${({ $highlighted }) =>
    $highlighted
      ? "var(--color-foreground, #404145)"
      : "var(--color-subtle, #7d7e84)"};
`;

export const Thumbnail = styled.div<{ $isAppIcon?: boolean }>`
  position: relative;
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  border-radius: ${({ $isAppIcon }) =>
    $isAppIcon ? "35%" : "var(--radius-sm, 4px)"};
  overflow: hidden;
  background: var(--color-white-100);
  display: flex;
  align-items: center;
  justify-content: center;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    z-index: 1;
    border-radius: inherit;
    box-shadow: inset 0 0 0 0.5px var(--color-gray-100, #e2e3e5);
    pointer-events: none;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const TitleBlock = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

export const Title = styled.span<{ $muted?: boolean }>`
  font-size: 1rem;
  line-height: 1.5rem;
  color: ${({ $muted }) =>
    $muted ? "var(--color-subtle, #7d7e84)" : "var(--color-gray-950, #19191b)"};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const Subtitle = styled.span`
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: var(--color-subtle, #7d7e84);
`;

export const Duration = styled.span`
  flex-shrink: 0;
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: var(--color-subtle, #7d7e84);
  font-variant-numeric: tabular-nums;
`;

/* Temporary find-target wash (Figma 3581:10563): blue-700 at 11% over the
   row, fading back out as the interaction completes. */
export const HighlightOverlay = styled.span`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: var(--color-blue-700, #0361f2);
  opacity: 0;
  animation: ${highlightFade} 2s ease forwards;
`;

/* Studio row-action pattern: borderless icon buttons inside the row, over
   its trailing edge, revealed by row hover or keyboard focus. They stay in the
   DOM so they remain focusable. The status label swaps out while they show
   (see StatusSlot), so the two never overlap. */
export const RowActions = styled.span`
  position: absolute;
  right: var(--spacing-md, 12px);
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-xxs, 2px);
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.1s ease;

  ${Row}:hover &,
  &:focus-within {
    opacity: 1;
    pointer-events: auto;
  }
`;

export const StatusSlot = styled.div<{ $yieldsToActions?: boolean }>`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  /* Fixed-width slot (reserved on both columns, even when empty) so the
     duration column lines up across rows regardless of a status label. */
  width: 100px;
  transition: opacity 0.1s ease;

  /* Rows with actions swap the label for them on hover or focus. */
  ${({ $yieldsToActions }) =>
    $yieldsToActions &&
    css`
      ${Row}:hover &,
      ${Row}:focus-within & {
        opacity: 0;
      }
    `}
`;
